import { ApiService } from "../../api/api-service";
import { getBatchJourneys } from "../hooks/journey.api";
import { JourneyItem } from "../Journey/journey.types";

/**
 * Calculate how many times an instructor has attendance = "Leave"
 * across all marked journey entries for a given batch.
 *
 * Works per batch + instructor — does NOT affect other instructors.
 */
export const getInstructorLeaveCount = async (
  batchNo: string,
  instructorEmail: string
): Promise<number> => {
  try {
    if (!batchNo || !instructorEmail) return 0;

    const journeys: JourneyItem[] = await getBatchJourneys(batchNo);

    // Only count marked journeys
    const markedJourneys = journeys.filter((j) => j.is_marked);

    let leaveCount = 0;

    for (const journey of markedJourneys) {
      if (!journey.instructors || journey.instructors.length === 0) continue;

      const instructor = journey.instructors.find(
        (ins) => ins.email.toLowerCase() === instructorEmail.toLowerCase()
      );

      if (instructor && instructor.attendance === "Leave") {
        leaveCount++;
      }
    }

    return leaveCount;
  } catch (error) {
    console.error("Error calculating instructor leave count:", error);
    return 0;
  }
};

/**
 * Calculate leave counts for ALL instructors in a batch in a SINGLE pass.
 * Returns a Map of email -> leaveCount so we don't fetch journeys N times.
 */
export const getAllInstructorLeaveCounts = async (
  batchNo: string,
  instructorEmails: string[]
): Promise<Map<string, number>> => {
  const result = new Map<string, number>();
  try {
    if (!batchNo || instructorEmails.length === 0) return result;

    const journeys: JourneyItem[] = await getBatchJourneys(batchNo);
    const markedJourneys = journeys.filter((j) => j.is_marked);

    // Initialize all emails with 0
    for (const email of instructorEmails) {
      result.set(email.toLowerCase(), 0);
    }

    // Count leaves in a single pass through all journeys
    for (const journey of markedJourneys) {
      if (!journey.instructors || journey.instructors.length === 0) continue;

      for (const ins of journey.instructors) {
        const emailKey = ins.email.toLowerCase();
        if (result.has(emailKey) && ins.attendance === "Leave") {
          result.set(emailKey, (result.get(emailKey) || 0) + 1);
        }
      }
    }

    return result;
  } catch (error) {
    console.error("Error calculating all instructor leave counts:", error);
    return result;
  }
};

/**
 * Sync leave counts for ALL instructors in a batch to the batch document.
 * Fetches journeys ONCE, calculates counts, and saves to batch if anything changed.
 *
 * Called:
 *  - From InstructorForm refresh button
 *  - After detecting stale counts on form mount
 */
export const syncBatchInstructorLeaves = async (
  batchNo: string
): Promise<boolean> => {
  try {
    if (!batchNo) return false;

    // 1. Fetch current batch
    const batchResponse = await ApiService.getInstance().get(
      `/batches/${batchNo}`
    );
    const currentBatch = batchResponse.data.data;
    if (!currentBatch) return false;

    const instructors =
      currentBatch.instructors || currentBatch.instructor || [];
    if (instructors.length === 0) return false;

    // 2. Fetch all journeys ONCE and calculate leaves for every instructor
    const emails = instructors.map((i: any) => i.email).filter(Boolean);
    const leaveCounts = await getAllInstructorLeaveCounts(batchNo, emails);

    // 3. Update instructor records
    let hasChanges = false;
    const updatedInstructors = instructors.map((ins: any) => {
      const emailKey = (ins.email || "").toLowerCase();
      const newCount = leaveCounts.get(emailKey) ?? 0;
      const oldCount = ins.trainerLeaves ?? 0;

      if (newCount !== oldCount) {
        hasChanges = true;
      }

      return { ...ins, trainerLeaves: newCount };
    });

    // 4. Save ONLY if something changed
    if (!hasChanges) return false;

    await ApiService.getInstance().put(`/batches/${batchNo}`, {
      ...currentBatch,
      instructors: updatedInstructors,
      instructor: updatedInstructors,
    });

    console.log(
      `Leave counts synced for batch ${batchNo}:`,
      Object.fromEntries(leaveCounts)
    );
    return true;
  } catch (error) {
    console.error("Error syncing batch instructor leaves:", error);
    return false;
  }
};
